import React, { useRef, useState } from 'react';
import { Dropdown, Tooltip } from 'antd';
import { BacklogTaskBanner } from '@/components/sprint/BacklogTaskBanner';
import {
    BugOutlined,
    CaretUpOutlined,
    CheckCircleOutlined,
    CloseOutlined,
    SendOutlined,
    UndoOutlined,
    DownOutlined,
    RightOutlined,
    PaperClipOutlined,
    EllipsisOutlined,
    EditOutlined,
    DeleteOutlined,
    InboxOutlined,
    PullRequestOutlined,
    RocketOutlined,
    RiseOutlined,
    CustomerServiceOutlined,
    WarningOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { GiUpgrade } from 'react-icons/gi';
import Markdown from '@/components/ui/Markdown';
import CompletionCelebration from '@/components/ui/CompletionCelebration';
import {
    getTaskDescriptionMarkdown,
    getTaskDisplayId,
    getTaskAttachmentCount,
    getTaskAttachmentListForModal,
    getSprintTaskApiId,
    getSprintTaskStatusLabel,
    getBugDescriptionMarkdown,
    getBugHeading,
    isTaskReturnedByQa,
    isTaskOnDevBacklog,
    isTaskMarkedIssueOrUpgrade,
    formatPriorityLabel,
    getInitials,
    getAvatarColor,
    getTaskKanbanFootprintIndicators,
    getTaskCardCreatorPersonStrict,
    getTaskCardAssigneePerson,
    getSprintTaskCreatedAtRaw,
} from '@hooks/projects/projectHelpers';
import { formatCreatedAtSentenceStable, formatAssignedAtSentenceStable } from '@utils/dateFormating.util';
import {
    CardDescriptionInner,
    TaskCardRemoveOverlay,
    TaskCardCircleRemoveButton,
    DisabledDimWrap,
    InactiveMilestoneWrap,
    SectionBadgeBug,
    TaskCardSurface,
    TaskCardTopMetaRow,
    TaskCardTaskIdLabel,
    TaskCardHeadingTitle,
    TaskCardRule,
    TaskCardDescScroll,
    TaskCardAttachmentsPill,
    TaskCardRevertFooter,
    TaskCardRevertLink,
    BoardNestedCard,
    BoardNestedCardBugId,
    BoardNestedCardPreview,
    MutedText,
    ModalBlockLabel,
    AvatarStackCircle,
    TaskCardKanbanStatusPill,
    TaskCardKanbanStatusDot,
} from '@/pages/inside/tenant/style';

/** Shared row height so attachment chip and plain footprint icons align. */
const FOOTER_BADGE_ROW_MIN_HEIGHT = 24;
const FOOTER_BADGE_ICON_PX = 13;

/** Kanban header: work-type icon and ⋯ control share the same hit height. */
const TASK_CARD_HEADER_TOOL_HIT = 28;
const TASK_CARD_WORK_TYPE_ICON_PX = 18;
/** Uniform horizontal gap between priority / type / overflow in card headers. */
const TASK_CARD_HEADER_CLUSTER_GAP = 8;
/** Pull stacked priority carets together (icon glyphs leave visual gap at gap:0). */
const PRIORITY_CARET_OVERLAP_PX = 5;

const footerIconChip = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '3px 8px',
    minHeight: FOOTER_BADGE_ROW_MIN_HEIGHT,
    boxSizing: 'border-box',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    border: '1px solid var(--ant-color-border-secondary, #ececec)',
    background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
    color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.65))',
};
/** Kanban footprint row: QA/bugs origin + PMO flags (aligned with {@link getTaskKanbanFootprintIndicators}). */
const TASK_CARD_FOOTPRINT_UI = {
    bug: { Icon: BugOutlined, color: '#E57373' },
    blocker: { Icon: WarningOutlined, color: '#fa8c16' },
    backlog: { Icon: InboxOutlined, color: '#6366f1' },
    changeRequest: { Icon: PullRequestOutlined, color: '#722ed1' },
    issueOrUpgrade: { Icon: GiUpgrade, color: '#1677ff' },
    clientTicket: { Icon: CustomerServiceOutlined, color: '#13c2c2' },
};

function kanbanFootprintChips(indicators) {
    return indicators.map((ind) => {
        const cfg = TASK_CARD_FOOTPRINT_UI[ind.key];
        if (!cfg) return null;
        const Icon = cfg.Icon;
        const c = cfg.color;
        return (
            <Tooltip key={ind.key} title={ind.tooltip}>
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: FOOTER_BADGE_ROW_MIN_HEIGHT,
                        boxSizing: 'border-box',
                        padding: '0 2px',
                        border: 'none',
                        background: 'transparent',
                        color: c,
                        lineHeight: 1,
                        flexShrink: 0,
                    }}
                    aria-label={ind.tooltip}
                >
                    <Icon style={{ fontSize: FOOTER_BADGE_ICON_PX }} aria-hidden />
                </span>
            </Tooltip>
        );
    });
}

/** Priority: 3 / 2 / 1 chevrons stacked in a column — red / orange / blue (task card header, no pill). */
function getPriorityArrowMeta(priorityRaw) {
    const p = String(priorityRaw ?? 'low').toLowerCase();
    if (p === 'high' || p === 'critical' || p === 'urgent') return { count: 3, color: '#DC2626' };
    if (p === 'medium') return { count: 2, color: '#EA580C' };
    return { count: 1, color: '#2563EB' };
}

function TaskCardPriorityArrowsOnly({ priority }) {
    const label = formatPriorityLabel(priority) || 'Low';
    const { count, color } = getPriorityArrowMeta(priority);
    const caretSize = 12;
    const arrowGapPx = 0;
    return (
        <Tooltip title={`Priority: ${label}`}>
            <span
                aria-label={`Priority ${label}`}
                style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-start',
                    gap: arrowGapPx,
                    color,
                    lineHeight: 1,
                }}
            >
                {Array.from({ length: count }, (_, i) => (
                    <CaretUpOutlined
                        key={i}
                        style={{
                            fontSize: caretSize,
                            display: 'block',
                            lineHeight: 0,
                            marginTop: i === 0 ? 0 : -PRIORITY_CARET_OVERLAP_PX,
                        }}
                        aria-hidden
                    />
                ))}
            </span>
        </Tooltip>
    );
}

function taskKanbanPersonTooltipSubtitle(name, subtitle, dateSentence) {
    const showDate = dateSentence && dateSentence !== '—';
    return (
        <div style={{ fontSize: 12, lineHeight: 1.35 }}>
            <div style={{ fontWeight: 600 }}>{name || '—'}</div>
            <div style={{ opacity: 0.85 }}>{subtitle}</div>
            {showDate && (
                <div style={{ opacity: 0.65, fontSize: 10, marginTop: 4 }}>
                    {dateSentence}
                </div>
            )}
        </div>
    );
}

const CREATOR_TOOLTIP_DELAY_MS = 420;

function TaskKanbanMetaAvatar({ creator, assignee, createdAtSentence, assignedAtSentence }) {
    const bothDistinct =
        Boolean(creator && assignee) &&
        ((creator.id != null && assignee.id != null && String(creator.id) !== String(assignee.id)) ||
            String(creator.name).trim().toLowerCase() !== String(assignee.name).trim().toLowerCase());

    const renderAvatar = (person, tooltipLabel, zIndex, dateSentence) => {
        if (!person) return null;
        const initials = getInitials(person.name);
        const color = getAvatarColor(String(person.id ?? person.name));
        return (
            <Tooltip title={taskKanbanPersonTooltipSubtitle(person.name, tooltipLabel, dateSentence)}>
                <AvatarStackCircle
                    $bg={color}
                    $color="#fff"
                    style={{
                        width: 26,
                        height: 26,
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 800,
                        lineHeight: 1,
                        boxSizing: 'border-box',
                        border: '1px solid var(--ant-color-border-secondary, #d9d9d9)',
                        cursor: 'default',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        zIndex,
                    }}
                    aria-label={person.name ? `Member: ${person.name}` : 'Member'}
                >
                    {initials}
                </AvatarStackCircle>
            </Tooltip>
        );
    };

    if (bothDistinct) {
        return (
            <div
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                style={{ flexShrink: 0, lineHeight: 0, display: 'inline-flex', alignItems: 'center' }}
                role="presentation"
            >
                {renderAvatar(creator, 'Creator', 1, createdAtSentence)}
                <div style={{ marginLeft: -8, zIndex: 2, position: 'relative' }}>
                    {renderAvatar(assignee, 'Assignee', 2, assignedAtSentence)}
                </div>
            </div>
        );
    }

    const display = assignee ?? creator;
    if (!display) return null;
    return (
        <div
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            style={{ flexShrink: 0, lineHeight: 0, display: 'inline-flex', alignItems: 'center' }}
            role="presentation"
        >
            {renderAvatar(display, assignee ? 'Assignee' : 'Creator', 1, assignee ? assignedAtSentence : createdAtSentence)}
        </div>
    );
}

function getNormalizedTaskWorkType(task) {
    const raw = task?.type ?? task?.taskType ?? task?.task_type;
    return String(raw ?? 'feature').toLowerCase();
}

/** Feature vs improvement marker (matches create-task modal types). */
function TaskCardWorkTypeIcon({ task }) {
    const t = getNormalizedTaskWorkType(task);
    const box = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: TASK_CARD_HEADER_TOOL_HIT,
        height: TASK_CARD_HEADER_TOOL_HIT,
        flexShrink: 0,
        lineHeight: 1,
    };
    if (t === 'improvement') {
        return (
            <Tooltip title="Improvement">
                <span role="img" aria-label="Improvement task type" style={box}>
                    <RiseOutlined style={{ fontSize: TASK_CARD_WORK_TYPE_ICON_PX, color: '#1677ff' }} aria-hidden />
                </span>
            </Tooltip>
        );
    }
    return (
        <Tooltip title="Feature">
            <span role="img" aria-label="Feature task type" style={box}>
                <RocketOutlined style={{ fontSize: TASK_CARD_WORK_TYPE_ICON_PX, color: '#7c3aed' }} aria-hidden />
            </span>
        </Tooltip>
    );
}

/**
 * Icon-only column / priority indicator (tooltips carry full status text).
 */
function TaskCardHeaderStatusIcon({
    kanbanColumn,
    task,
    priority,
    bugsColumnStatusVariant,
    bugsColumnStatusLabel,
}) {
    if (bugsColumnStatusVariant === 'returned') {
        const title = bugsColumnStatusLabel || 'Returned by QA';
        return (
            <Tooltip title={title}>
                <span style={{ display: 'inline-flex', lineHeight: 1 }}>
                    <UndoOutlined style={{ fontSize: 16, color: '#dc2626' }} aria-hidden />
                </span>
            </Tooltip>
        );
    }
    if (kanbanColumn === 'completed' || kanbanColumn === 'submitted' || kanbanColumn === 'resolved') {
        const statusLabel = getSprintTaskStatusLabel(task);
        if (kanbanColumn === 'submitted') {
            return (
                <Tooltip title={statusLabel}>
                    <span style={{ display: 'inline-flex', lineHeight: 1 }}>
                        <SendOutlined style={{ fontSize: 16, color: '#7543DC' }} aria-hidden />
                    </span>
                </Tooltip>
            );
        }
        return (
            <Tooltip title={statusLabel}>
                <span style={{ display: 'inline-flex', lineHeight: 1 }}>
                    <CheckCircleOutlined style={{ fontSize: 17, color: '#34a853' }} aria-hidden />
                </span>
            </Tooltip>
        );
    }
    if (kanbanColumn === 'bugs') {
        const title = bugsColumnStatusLabel || getSprintTaskStatusLabel(task);
        if (bugsColumnStatusVariant === 'backlog') {
            return (
                <Tooltip title={title}>
                    <span style={{ display: 'inline-flex', lineHeight: 1 }}>
                        <InboxOutlined style={{ fontSize: 16, color: '#d46b08' }} aria-hidden />
                    </span>
                </Tooltip>
            );
        }
        return (
            <Tooltip title={title}>
                <span style={{ display: 'inline-flex', lineHeight: 1 }}>
                    <BugOutlined style={{ fontSize: 16, color: '#ea580c' }} aria-hidden />
                </span>
            </Tooltip>
        );
    }
    return <TaskCardPriorityArrowsOnly priority={priority} />;
}

function KanbanTaskOverflowMenuTrigger({ task, onOverflowEdit, onOverflowDelete, onOverflowAssign }) {
    const [hover, setHover] = useState(false);
    const baseColor = 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))';
    const hoverColor = 'var(--ant-color-primary, #1677ff)';
    const items = [
        {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
            disabled: typeof onOverflowEdit !== 'function',
        },
        {
            key: 'assign',
            icon: <UserOutlined />,
            label: 'Assign user',
            disabled: typeof onOverflowAssign !== 'function',
        },
        {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
            disabled: typeof onOverflowDelete !== 'function',
        },
    ];
    return (
        <Dropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{
                items,
                onClick: ({ key, domEvent }) => {
                    domEvent?.stopPropagation?.();
                    if (key === 'edit' && typeof onOverflowEdit === 'function') onOverflowEdit(task);
                    else if (key === 'assign' && typeof onOverflowAssign === 'function') onOverflowAssign(task);
                    else if (key === 'delete' && typeof onOverflowDelete === 'function') {
                        onOverflowDelete(task);
                    }
                },
            }}
        >
            <button
                type="button"
                aria-label="Task actions"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                style={{
                    padding: 0,
                    margin: 0,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: TASK_CARD_HEADER_TOOL_HIT,
                    height: TASK_CARD_HEADER_TOOL_HIT,
                    flexShrink: 0,
                    color: hover ? hoverColor : baseColor,
                    borderRadius: 0,
                }}
            >
                <EllipsisOutlined style={{ fontSize: 18, color: 'currentColor', transform: 'rotate(90deg)' }} aria-hidden />
            </button>
        </Dropdown>
    );
}

/**
 * Global sprint task card. Same look everywhere.
 * variant: 'editable' | 'readOnly' | 'compact'
 */
export function SprintTaskCard({
    task,
    qaSubmissions = [],
    variant = 'readOnly',
    onMarkDone: _onMarkDone,
    onRevertToTodo,
    draggable,
    onDragStart,
    isDisabled,
    onRemove,
    isCelebrating,
    onCelebrationComplete,
    onSelect,
    /** Kanban / board: open read-only detail modal (Sprints, QA-style). */
    onOpenDetail,
    hideCompletedBadge: _hideCompletedBadge,
    alwaysExpanded: _alwaysExpanded,
    bugs,
    onBugSelect,
    bugsExpanded,
    onBugsExpandChange,
    isMilestoneInactive = false,
    /** Sprint Kanban: open-hand grab cursor; drag handled by outer wrapper */
    kanbanGrabCursor = false,
    /** Kanban column: todo | completed | submitted | bugs — fill/stroke + layout */
    kanbanColumn,
    /** Apply column fill/tint without drag (e.g. QA read-only board). */
    kanbanColumnTintOnly = false,
    /** Sprints Kanban: Edit in ⋯ menu (RBAC decided by parent). */
    onOverflowEdit,
    /** Sprints Kanban: Delete in ⋯ menu */
    onOverflowDelete,
    /** Sprints Kanban: Assign user (opens modal; RBAC in parent). */
    onOverflowAssign,
    /** Drag overlay etc.: hide ⋯ */
    suppressOverflowMenu = false,
}) {
    const taskNumberDisplay =
        task?.taskNumber || task?.task_number || task?.number || getTaskDisplayId(task);
    const descriptionHtml = getTaskDescriptionMarkdown(task);
    const attachmentItems = getTaskAttachmentListForModal(task);
    const hasDescription = descriptionHtml.length > 0;
    const statusLower = String(task?.status).toLowerCase();
    const isDone = statusLower === 'done' || statusLower === 'completed';
    const isSubmittedToQa = statusLower === 'submitted_to_qa';
    const isSubmittedToPm = statusLower === 'submitted_to_pm';
    const hasBugs = Array.isArray(bugs) && bugs.length > 0;
    const [internalBugsExpanded, setInternalBugsExpanded] = useState(false);
    const expanded = bugsExpanded ?? internalBugsExpanded;

    const cameFromQa = Boolean(task?.cameFromQa || task?.came_from_qa);
    const matchedSubmission = React.useMemo(() => {
        if (!cameFromQa || !Array.isArray(qaSubmissions)) return null;
        const taskId = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
        if (taskId == null) return null;
        return qaSubmissions.find(sub =>
            Array.isArray(sub?.tasks) && sub.tasks.some(tId => String(tId) === String(taskId))
        );
    }, [cameFromQa, qaSubmissions, task]);

    const submissionLast4 = React.useMemo(() => {
        if (!matchedSubmission) return '';
        const rawId = String(matchedSubmission.submissionId || matchedSubmission.id || '');
        if (!rawId) return '';
        return rawId.length > 4 ? rawId.slice(-4) : rawId;
    }, [matchedSubmission]);

    const priority = task?.priority || 'low';
    const attachmentCount = getTaskAttachmentCount(task);
    const showAttachments = attachmentCount > 0;
    const showQaReturn = isTaskReturnedByQa(task);
    const showBacklogTask = isTaskOnDevBacklog(task);

    if (variant === 'compact') {
        const compactCard = (
            <TaskCardSurface>
                <TaskCardTopMetaRow style={{ marginBottom: 8 }}>
                    <TaskCardTaskIdLabel>{taskNumberDisplay}</TaskCardTaskIdLabel>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: TASK_CARD_HEADER_CLUSTER_GAP,
                            }}
                        >
                            <TaskCardWorkTypeIcon task={task} />
                            <TaskCardPriorityArrowsOnly priority={priority} />
                        </span>
                </TaskCardTopMetaRow>
                <TaskCardHeadingTitle $lineClamp1 style={{ marginBottom: 0, fontSize: 15, fontWeight: 600 }}>
                    {task?.name ?? task?.title ?? '—'}
                </TaskCardHeadingTitle>
                {onRemove && (
                    <TaskCardRemoveOverlay>
                        <TaskCardCircleRemoveButton
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove(getSprintTaskApiId(task) ?? task?.id);
                            }}
                            aria-label="Remove task"
                        >
                            <CloseOutlined style={{ fontSize: 12 }} />
                        </TaskCardCircleRemoveButton>
                    </TaskCardRemoveOverlay>
                )}
            </TaskCardSurface>
        );
        if (isMilestoneInactive) {
            return <InactiveMilestoneWrap $inactive={true}>{compactCard}</InactiveMilestoneWrap>;
        }
        return compactCard;
    }

    const toggleBugsExpanded = () => {
        if (onBugsExpandChange) onBugsExpandChange(!expanded);
        else setInternalBugsExpanded((prev) => !prev);
    };

    const handleCardClick = () => {
        if (onOpenDetail) {
            onOpenDetail(task);
            return;
        }
        if (kanbanColumn === 'bugs' && onSelect && variant === 'readOnly') {
            onSelect(task);
            return;
        }
        if (hasBugs) {
            toggleBugsExpanded();
        } else if (onSelect && variant === 'readOnly') {
            onSelect(task);
        }
    };

    const handleBugHeaderClick = (e) => {
        if (!hasBugs) return;
        e.stopPropagation();
        toggleBugsExpanded();
    };

    const cardClickable =
        (hasBugs || onOpenDetail || (onSelect && variant === 'readOnly')) && !draggable;

    const surfaceCursorStyle =
        kanbanGrabCursor || (draggable && !isDisabled)
            ? undefined
            : { cursor: cardClickable ? 'pointer' : 'default' };

    const showKanbanColumnStyles = Boolean(kanbanColumn && (kanbanGrabCursor || kanbanColumnTintOnly));

    const bugsColumnStatusVariant = showQaReturn ? 'returned' : showBacklogTask ? 'backlog' : 'bugs';
    const bugsColumnStatusLabel = showQaReturn ? 'Returned by QA' : showBacklogTask ? 'Backlog Task' : getSprintTaskStatusLabel(task);

    const professionalKanbanLayout = variant !== 'compact' && showKanbanColumnStyles;
    const footprintIndicators = getTaskKanbanFootprintIndicators(task, {
        bugsExternalCount: Array.isArray(bugs) ? bugs.length : 0,
    });

    const createdAtRaw = getSprintTaskCreatedAtRaw(task);
    const createdAtSentence = formatCreatedAtSentenceStable(createdAtRaw);
    const assignedAtRaw = task?.assignedAt ?? task?.assigned_at;
    const assignedAtSentence = formatAssignedAtSentenceStable(assignedAtRaw);

    const creatorPersonStrict = getTaskCardCreatorPersonStrict(task);
    const assigneePerson = getTaskCardAssigneePerson(task);

    const hideOverflowForQaReturn = isTaskReturnedByQa(task);
    const showOverflowDropdown =
        professionalKanbanLayout &&
        !suppressOverflowMenu &&
        !hideOverflowForQaReturn &&
        (typeof onOverflowEdit === 'function' ||
            typeof onOverflowDelete === 'function' ||
            typeof onOverflowAssign === 'function');

    /** Status / priority (between work-type icon and ⋯ overflow). */
    const rightStatusBadge = (
        <div
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                minHeight: TASK_CARD_HEADER_TOOL_HIT,
            }}
        >
            <TaskCardHeaderStatusIcon
                kanbanColumn={kanbanColumn}
                task={task}
                priority={priority}
                bugsColumnStatusVariant={bugsColumnStatusVariant}
                bugsColumnStatusLabel={bugsColumnStatusLabel}
            />
        </div>
    );

    const card = (
        <TaskCardSurface
            $kanbanGrab={kanbanGrabCursor}
            $kanbanColumn={showKanbanColumnStyles ? kanbanColumn : undefined}
            $kanbanMuteHover={kanbanColumn === 'submitted'}
            draggable={draggable && !isDisabled}
            onDragStart={draggable && !isDisabled && onDragStart ? (e) => onDragStart(e, task) : undefined}
            onClick={cardClickable ? handleCardClick : undefined}
            style={surfaceCursorStyle}
        >
            {isCelebrating && <CompletionCelebration play inline onComplete={onCelebrationComplete} />}

            {professionalKanbanLayout ? (
                <>
                    {showBacklogTask && kanbanColumn !== 'bugs' ? (
                        <div style={{ marginBottom: 10 }}>
                            <BacklogTaskBanner />
                        </div>
                    ) : null}

                    {isTaskMarkedIssueOrUpgrade(task) && kanbanColumn !== 'bugs' ? (
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <GiUpgrade style={{ fontSize: 16, color: '#1677ff', flexShrink: 0 }} />
                                <TaskCardKanbanStatusPill $variant="issueOrUpgrade" style={{ marginBottom: 0 }}>
                                    <TaskCardKanbanStatusDot $variant="issueOrUpgrade" aria-hidden />
                                    {task?.issueOrUpgradeId?.issuesAndUpgradesNumber ? `Issue: ${task.issueOrUpgradeId.issuesAndUpgradesNumber}` : 'Issue & Upgrade'}
                                </TaskCardKanbanStatusPill>
                            </div>
                        </div>
                    ) : null}

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 10,
                            marginBottom: 10,
                        }}
                    >
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <TaskCardTaskIdLabel $kanbanColumn={kanbanColumn}>
                                {taskNumberDisplay}
                            </TaskCardTaskIdLabel>
                            <TaskCardHeadingTitle
                                $lineClamp1={false}
                                $kanbanColumn={kanbanColumn}
                                style={{ WebkitLineClamp: 2, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                            >
                                {task?.title ?? task?.name ?? '—'}
                            </TaskCardHeadingTitle>
                        </div>
                        <div
                            style={{
                                flexShrink: 0,
                                display: 'inline-flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: TASK_CARD_HEADER_CLUSTER_GAP,
                                alignSelf: 'flex-start',
                                paddingTop: 1,
                            }}
                        >
                            <TaskCardWorkTypeIcon task={task} />
                            {rightStatusBadge}
                            {showOverflowDropdown ? (
                                <KanbanTaskOverflowMenuTrigger
                                    task={task}
                                    onOverflowEdit={onOverflowEdit}
                                    onOverflowDelete={onOverflowDelete}
                                    onOverflowAssign={onOverflowAssign}
                                />
                            ) : null}
                        </div>
                    </div>

                    <TaskCardRule $kanbanColumn={kanbanColumn} />
                    <ModalBlockLabel as="div" style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                        Description
                    </ModalBlockLabel>
                    {hasDescription ? (
                        <TaskCardDescScroll $kanbanColumn={kanbanColumn} $previewLines={3}>
                            <Markdown
                                source={descriptionHtml}
                                component={CardDescriptionInner}
                                richInlineAnchors
                                attachmentItems={attachmentItems}
                                linkPrimary
                            />
                        </TaskCardDescScroll>
                    ) : (
                        <MutedText style={{ margin: '0 0 10px 0', fontSize: 13 }}>No description</MutedText>
                    )}

                    <TaskCardRule $kanbanColumn={kanbanColumn} style={{ marginTop: 12 }} />

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 10,
                            flexWrap: 'wrap',
                        }}
                    >
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={footerIconChip} aria-label={`${attachmentCount} attachments`}>
                                <PaperClipOutlined style={{ fontSize: FOOTER_BADGE_ICON_PX }} aria-hidden />
                                <span>{attachmentCount}</span>
                            </span>
                            {kanbanFootprintChips(footprintIndicators)}
                            {cameFromQa && (
                                <Tooltip title={matchedSubmission ? `QA-${matchedSubmission.submissionId || matchedSubmission.id}` : "Came from QA"}>
                                    <BugOutlined style={{ fontSize: 13, color: '#E57373', cursor: 'pointer', marginLeft: 4 }} />
                                </Tooltip>
                            )}
                        </div>
                        <div
                            style={{
                                display: 'inline-flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 10,
                                flexShrink: 0,
                                minWidth: 0,
                                maxWidth: '100%',
                                marginLeft: 'auto',
                                textAlign: 'right',
                            }}
                        >
                            <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>
                                <TaskKanbanMetaAvatar
                                    creator={creatorPersonStrict}
                                    assignee={assigneePerson}
                                    createdAtSentence={createdAtSentence}
                                    assignedAtSentence={assignedAtSentence}
                                />
                            </span>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <TaskCardTopMetaRow $kanbanColumn={showKanbanColumnStyles ? kanbanColumn : undefined}>
                        <TaskCardTaskIdLabel $kanbanColumn={showKanbanColumnStyles ? kanbanColumn : undefined}>
                            {taskNumberDisplay}
                        </TaskCardTaskIdLabel>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: TASK_CARD_HEADER_CLUSTER_GAP,
                            }}
                        >
                            <TaskCardWorkTypeIcon task={task} />
                            <TaskCardHeaderStatusIcon
                                kanbanColumn={kanbanColumn}
                                task={task}
                                priority={priority}
                                bugsColumnStatusVariant={bugsColumnStatusVariant}
                                bugsColumnStatusLabel={bugsColumnStatusLabel}
                            />
                        </span>
                    </TaskCardTopMetaRow>

                    <TaskCardHeadingTitle
                        $lineClamp1={kanbanGrabCursor || kanbanColumnTintOnly}
                        $kanbanColumn={showKanbanColumnStyles ? kanbanColumn : undefined}
                    >
                        {task?.title ?? task?.name ?? '—'}
                    </TaskCardHeadingTitle>

                    {showBacklogTask && kanbanColumn !== 'bugs' ? (
                        <div style={{ marginBottom: 10 }}>
                            <BacklogTaskBanner />
                        </div>
                    ) : null}

                    {isTaskMarkedIssueOrUpgrade(task) && kanbanColumn !== 'bugs' ? (
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <GiUpgrade style={{ fontSize: 16, color: '#1677ff', flexShrink: 0 }} />
                                <TaskCardKanbanStatusPill $variant="issueOrUpgrade" style={{ marginBottom: 0 }}>
                                    <TaskCardKanbanStatusDot $variant="issueOrUpgrade" aria-hidden />
                                    {task?.issueOrUpgradeId?.issuesAndUpgradesNumber ? `Issue: ${task.issueOrUpgradeId.issuesAndUpgradesNumber}` : 'Issue & Upgrade'}
                                </TaskCardKanbanStatusPill>
                            </div>
                        </div>
                    ) : null}

                    {hasDescription && (
                        <>
                            <TaskCardRule />
                            <TaskCardDescScroll
                                $kanbanColumn={showKanbanColumnStyles ? kanbanColumn : undefined}
                                $previewLines={showKanbanColumnStyles ? 3 : undefined}
                            >
                                <Markdown
                                    source={descriptionHtml}
                                    component={CardDescriptionInner}
                                    richInlineAnchors
                                    attachmentItems={attachmentItems}
                                    linkPrimary
                                />
                            </TaskCardDescScroll>
                        </>
                    )}

                    {(showAttachments || footprintIndicators.length > 0 || cameFromQa) && (
                        <>
                            {(hasDescription ||
                                (showBacklogTask && kanbanColumn !== 'bugs') ||
                                footprintIndicators.length > 0 ||
                                cameFromQa) && (
                                    <TaskCardRule $kanbanColumn={showKanbanColumnStyles ? kanbanColumn : undefined} />
                                )}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {showAttachments ? (
                                    <TaskCardAttachmentsPill $kanbanColumn={showKanbanColumnStyles ? kanbanColumn : undefined}>
                                        <PaperClipOutlined style={{ fontSize: 13 }} aria-hidden />
                                        Attachments
                                        {attachmentCount > 1 ? ` (${attachmentCount})` : ''}
                                    </TaskCardAttachmentsPill>
                                ) : null}
                                {kanbanFootprintChips(footprintIndicators)}
                                {cameFromQa && (
                                    <Tooltip title={matchedSubmission ? `QA-${matchedSubmission.submissionId || matchedSubmission.id}` : "Came from QA"}>
                                        <BugOutlined style={{ fontSize: 13, color: '#E57373', cursor: 'pointer', marginLeft: 4 }} />
                                    </Tooltip>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}

            {hasBugs && kanbanColumn === 'bugs' && (
                <>
                    <TaskCardRule $kanbanColumn={showKanbanColumnStyles ? kanbanColumn : undefined} />
                    <MutedText as="div" style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827' }}>
                        Total Bugs: {bugs.length}
                    </MutedText>
                </>
            )}

            {hasBugs && kanbanColumn !== 'bugs' && (
                <>
                    <div
                        role={onOpenDetail ? 'button' : undefined}
                        tabIndex={onOpenDetail ? 0 : undefined}
                        onClick={onOpenDetail ? handleBugHeaderClick : undefined}
                        onKeyDown={
                            onOpenDetail
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleBugHeaderClick(e);
                                    }
                                }
                                : undefined
                        }
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            marginTop: kanbanColumn ? 0 : 12,
                            marginBottom: kanbanColumn ? 0 : 4,
                            cursor: onOpenDetail ? 'pointer' : undefined,
                        }}
                    >
                        {expanded ? <DownOutlined style={{ fontSize: 12 }} /> : <RightOutlined style={{ fontSize: 12 }} />}
                        <SectionBadgeBug>Bugs ({bugs.length})</SectionBadgeBug>
                    </div>
                    <div
                        style={{
                            overflow: 'hidden',
                            maxHeight: expanded ? 2000 : 0,
                            transition: 'max-height 0.25s ease-out',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                                paddingTop: 4,
                            }}
                        >
                            {bugs.map((bug, idx) => {
                                const previewText = getBugHeading({
                                    ...bug,
                                    description: getBugDescriptionMarkdown(bug) || bug.description,
                                });
                                return (
                                    <BoardNestedCard
                                        key={bug.id ?? `bug-${idx}`}
                                        role={onBugSelect ? 'button' : undefined}
                                        tabIndex={onBugSelect ? 0 : undefined}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onBugSelect?.(bug);
                                        }}
                                        onKeyDown={(e) => {
                                            if (onBugSelect && (e.key === 'Enter' || e.key === ' ')) {
                                                e.stopPropagation();
                                                onBugSelect?.(bug);
                                            }
                                        }}
                                        style={{
                                            cursor: onBugSelect ? 'pointer' : 'default',
                                            textAlign: 'left',
                                        }}
                                    >
                                        <BoardNestedCardBugId>BUG-{idx + 1}</BoardNestedCardBugId>
                                        <BoardNestedCardPreview title={previewText}>{previewText}</BoardNestedCardPreview>
                                    </BoardNestedCard>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}

            {/* {variant === 'editable' && isDone && onRevertToTodo && !kanbanGrabCursor && (
                <TaskCardRevertFooter>
                    <TaskCardRevertLink
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRevertToTodo(getSprintTaskApiId(task) ?? task?.id);
                        }}
                    >
                        <UndoOutlined style={{ fontSize: 12 }} />
                        Move back to to-do
                    </TaskCardRevertLink>
                </TaskCardRevertFooter>
            )} */}

            {variant === 'readOnly' && onRemove && (
                <TaskCardRemoveOverlay>
                    <TaskCardCircleRemoveButton
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove(getSprintTaskApiId(task) ?? task?.id);
                        }}
                        aria-label="Remove task"
                    >
                        <CloseOutlined style={{ fontSize: 12 }} />
                    </TaskCardCircleRemoveButton>
                </TaskCardRemoveOverlay>
            )}
        </TaskCardSurface>
    );

    if (isDisabled) {
        return <DisabledDimWrap $disabled={true}>{card}</DisabledDimWrap>;
    }
    if (isMilestoneInactive) {
        return <InactiveMilestoneWrap $inactive={true}>{card}</InactiveMilestoneWrap>;
    }
    return card;
}
